import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AdRequest, PlacementAdRequest } from '../models';
import { AdRequestPostRequest } from '../models/ad-request-post-request.model';
import { AdRequestPostResponse } from '../models/ad-request-post-response.model';
import { AlloxApiService } from './allox-api.service';

/**
 * 広告リクエストの検索条件を表すインターフェース.
 *
 * @interface IAdRequestSearchParameter
 */
interface IAdRequestSearchParameter {
  toURLQuery(): string;
}

interface IAdRequestFetchParameter {
  placement_id?: number;
}
/**
 * 広告リクエストの検索条件（プレースメントID群）を表すインターフェース.
 *
 * @interface IAdRequestSearchParameters
 */
interface IAdRequestFetchParameters {
  placement_ids: number[];
}

/**
 * 削除する広告リクエストを表すインターフェース.
 *
 * @interface IAdRequestDeleteParameter
 */
interface IAdRequestDeleteParameter {
  toDeleteItemJSON(): any;
}

/**
 * 広告リクエストサービス
 *
 * @exports
 */
@Injectable({ providedIn: 'root' })
export class AdRequestService {
  private path = '/ad_requests';

  constructor(private http: AlloxApiService) {}

  /**
   * 広告リクエストを検索する
   *
   * @param {IAdRequestSearchParameter} param - 検索条件
   * @returns {Observable<AdRequest[]>} 広告リクエスト一覧のObservable
   */
  public search(param: IAdRequestSearchParameter) {
    const query = param.toURLQuery();
    return this.http
      .get<[]>(this.path + query)
      .pipe(map((objs) => objs.map((obj) => new AdRequest(obj))));
  }

  /**
   * プレースメントIDで該当する広告リクエストを取得する（GETメソッド）.
   *
   * @param {IAdRequestFetchParameter} param - 検索条件（プレースメントID）
   * @returns {Observable<AdRequest[]>} 広告リクエスト一覧のObservable
   */
  public fetchByPlacementId(param: IAdRequestFetchParameter) {
    const query = Object.keys(param)
      .filter(k => param[k] != null)
      .reduce((prev, k) => prev += '&' + k + '=' + encodeURIComponent(param[k]), '')
      .replace('&', '?');
    return this.http.get<[]>('/placements'+this.path + query).pipe(
      map(objs => objs.map(obj => new PlacementAdRequest(obj)))
    );
  }

  /**
   * プレースメントID群で該当する広告リクエストを取得する（POSTメソッド）.
   *
   * @param {IAdRequestFetchParameters} param - 検索条件（プレースメントID群）
   * @returns {Observable<AdRequest[]>} 広告リクエスト一覧のObservable
   */
  public fetchByPlacementIds(param: IAdRequestFetchParameters) {
    return this.http
      .post<PlacementAdRequest[]>('/placements'+this.path, param)
      .pipe(map((objs) => objs.map((obj) => new PlacementAdRequest(obj))));
  }

  /**
   * 広告リクエストIDで広告リクエストを取得する.
   *
   * @param {number} id - 広告リクエストID
   * @returns {Observable<AdRequest>} 広告リクエストのObservable
   */
  public fetch(id: number) {
    return this.http.get(this.path + '/' + id).pipe(map((obj) => new AdRequest(obj)));
  }

  /**
   * 広告リクエストを削除する.
   *
   * @param items - 削除対象の広告リクエストを表すIAdRequestDeleteParameterの配列
   * @returns HTTP responseのObservable
   */
  public delete(items: IAdRequestDeleteParameter[]) {
    const dItems = items.map((item) => item.toDeleteItemJSON());
    return this.http.post(this.path + '/delete', { delete_items: dItems });
  }

  /**
   * 広告リクエストを登録する.
   *
   * @param item - 登録内容の情報.
   * @returns AdRequestPostResponseのObservable
   */
  public post(item: AdRequestPostRequest): Observable<AdRequestPostResponse> {
    const json = item.toJSON();
    return this.http.post<AdRequestPostResponse>(this.path, json);
  }

  /**
   * 広告リクエストを更新する.
   * @param item
   * @returns
   */
  public put(item: AdRequestPostRequest): Observable<AdRequestPostResponse> {
    const json = item.toJSON();
    return this.http.put<AdRequestPostResponse>(this.path + '/' + item.id, json);
  }
}
